import { useEffect } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import { useContext } from 'react';

import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Button,
  Tooltip,
  Divider,
} from '@chakra-ui/react';

import ValidationMessage from 'components/validation/ValidationMessage';

import SharedTextFieldInput from '../TextFieldInput';

import getValidationMessage from 'helpers/getValidationMessage';

import ValidateFile from './components/ValidateFile';
import ValidateText from './components/ValidateText';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import { SharedMessageProps } from './Message.types';

import { APIRemark, APIRemarkFile } from 'types/api.types';

import { getMandatoryFileValidations } from 'views/shared/ReferToUnderwriting/helpers';

const SharedMessage = (props: SharedMessageProps) => {
  // commercialForm has no remarks/files state slice, so the remark/file
  // workflow this component drives does not apply.
  if (props.formStateKey === 'commercialForm') {
    return null;
  }

  return <SharedMessageInner {...props} formStateKey={props.formStateKey} />;
};

type InnerProps = Omit<SharedMessageProps, 'formStateKey'> & {
  formStateKey: 'autoPersonalForm' | 'homePersonalForm';
};

const SharedMessageInner = (props: InnerProps) => {
  const { uuid } = useParams();
  const { validations } = useContext(ApplicationFormContext);
  const {
    isOpen: isCompactOpen,
    onOpen: onCompactOpen,
    onClose: onCompactClose,
  } = useDisclosure();
  const dispatch = useAppDispatch();

  const mandatoryFileValidations = getMandatoryFileValidations(
    props.formStateKey,
  );

  const key = props?.storageKey
    ? `${props.storageKey}-${props.index}`
    : `global`;
  const validationId = props.validationId;

  const alertMessage = props?.manual?.show
    ? props?.manual?.message
    : getValidationMessage(validations, validationId, props?.index);
  const show = !!alertMessage;
  const showRemarks = show && !props?.hideRemarks;
  const showFile = show && !!props?.file;

  const remark = useAppSelector((state) => {
    const remark = state[props.formStateKey].remarks.find(
      (r: any) => r.validation_key === validationId && r.item_key === key,
    );
    return remark;
  });

  const file = useAppSelector((state) => {
    const file = state[props.formStateKey].files.find(
      (f: any) => f.validation_key === validationId && f.item_key === key,
    );
    return file;
  });

  const getHTMLId = () => {
    if (key === 'global') return validationId;

    return `${validationId}-${key}`;
  };

  const onRemarksChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      props.formActions.updateRemark({
        validation_key: validationId,
        item_key: key,
        data: {
          text: e.target.value,
          status: 'created',
        },
      }),
    );
  };

  const getCompactAlertStatus = (remark: APIRemark, file: APIRemarkFile) => {
    if (props.renderRemarkInput) {
      if (!remark?.text || remark?.status !== 'approved') return 'warning';
    } else if (!props.hideRemarks && remark?.status !== 'approved') {
      return 'warning';
    }
    if (file && file.status !== 'approved' && !file.is_deferred)
      return 'warning';
    return 'success';
  };

  useEffect(() => {
    if (showRemarks) {
      dispatch(
        props.formActions.addRemark({
          validation_key: validationId,
          item_key: key,
          text: '',
          status: 'created',
          text_validator_log_id: null,
          ai_result: '',
          section: props.section,
          ...(props.remarkCustomData
            ? { custom_data: props.remarkCustomData }
            : {}),
        }),
      );
    } else if (!show) {
      dispatch(
        props.formActions.removeRemark({
          validation_key: validationId,
          item_key: key,
        }),
      );
    }
  }, [showRemarks, show]);

  useEffect(() => {
    if (!props.file) return;

    if (showFile) {
      dispatch(
        props.formActions.addFile({
          validation_key: validationId,
          item_key: key,
          status: 'created',
          file_validator_log_id: null,
          ai_result: '',
          section: props.section,
          type: props.file.type,
          files: [],
          text: props.file.text,
          mandatory:
            props.file?.mandatory !== undefined
              ? props.file.mandatory
              : mandatoryFileValidations.includes(validationId),
        }),
      );
    } else {
      dispatch(
        props.formActions.removeFile({
          validation_key: validationId,
          item_key: key,
        }),
      );
    }
  }, [showFile, props.file]);

  if (!show) return null;

  if (props?.compact) {
    return (
      <>
        <div id={getHTMLId()}></div>
        <Tooltip placement="top" label={alertMessage}>
          <Alert
            status={props.compactStatus ?? getCompactAlertStatus(remark, file)}
            mt="10px"
            fontSize="14px"
          >
            <Button
              onClick={() => onCompactOpen()}
              w="100%"
              whiteSpace="normal"
            >
              <AlertIcon />
              {props.compactLabel ??
                (!props.hideRemarks ? 'Add remark' : 'Upload file')}
            </Button>
          </Alert>
        </Tooltip>
        <Modal isOpen={isCompactOpen} onClose={onCompactClose} size="4xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Remarks details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {(!props.hideMessage || !props.hideRemarks || props.file) && (
                <Box mb="10px">
                  <SharedMessage {...props} compact={false} />
                </Box>
              )}
              {props.modalExtraContent && (
                <Box mb="10px">{props.modalExtraContent}</Box>
              )}
              <Button
                variant="brand"
                onClick={() => onCompactClose()}
                mb="15px"
                float="right"
              >
                Close
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <>
      <div id={getHTMLId()}></div>

      {!props.hideMessage && (
        <Alert status="warning" mt="10px" fontSize="14px">
          <AlertIcon />
          <ValidationMessage message={alertMessage} />
        </Alert>
      )}

      {showRemarks && remark && (
        <Box mt="10px">
          {props.renderRemarkInput ? (
            <props.renderRemarkInput
              remark={remark}
              formStateKey={props.formStateKey}
              section={props.section}
              onUpdateRemark={(data) =>
                dispatch(
                  props.formActions.updateRemark({
                    validation_key: validationId,
                    item_key: key,
                    data: {
                      text_validator_log_id: null,
                      ai_result: '',
                      status: 'created',
                      ...data,
                    },
                  }),
                )
              }
            />
          ) : (
            <SharedTextFieldInput
              label={props.remarkLabel ?? 'Remarks'}
              value={remark.text}
              onChange={onRemarksChange}
              borderColor={remark.status !== 'approved' ? 'red' : 'green'}
              formStateKey={props.formStateKey}
              section={props.section}
            />
          )}

          {!props.renderRemarkInput && (
            <ValidateText
              uuid={uuid}
              remark={remark}
              validationId={validationId}
              itemKey={key}
              formStateKey={props.formStateKey}
              context={String(alertMessage ?? '')}
            />
          )}
        </Box>
      )}

      {showRemarks && remark && file && <Divider mt="10px" />}

      {file && (
        <ValidateFile
          uuid={uuid}
          file={file}
          validationId={validationId}
          itemKey={key}
          formStateKey={props.formStateKey}
          context={String(alertMessage ?? '')}
        />
      )}
    </>
  );
};

export default SharedMessage;
